// De formulieren van de 43 CRM-acties (contract §6.3, vroeger public/js/actions.js).
// Labels, velden en vaste keuzes staan in shared/actions.json (gelijk aan de invoercontrole van de
// backend, bewaakt door test/shared.test.ts). Hier staat wat van de data afhangt: welke records je
// kan kiezen en welke waarden al ingevuld staan.
import actionsJson from "@shared/actions.json";
import type { Crm } from "@/hooks/useCrm";
import { ACTIVE_CONTRACT_PHASES, OPEN_TASK, OPEN_TICKET, firstId, ids, str, CHECKLIST } from "./crm";
import { addDays, daysUntil, fmtDate, todayIso } from "./dates";
import type { ActionContext, CrmRecord, TableKey } from "./types";

export interface ActionField {
  name: string;
  label: string;
  kind: "text" | "textarea" | "email" | "date" | "time" | "number" | "select" | "multi" | "record" | "records";
  required: boolean;
  options?: string[];
  optionLabels?: Record<string, string>;
  table?: TableKey;
  context?: keyof ActionContext;
  default?: string;
  /** Datumveld dat leeg begint (anders: vandaag + een paar dagen). */
  noAutoDate?: boolean;
}

export interface ActionDef {
  id: string;
  label: string;
  module: string;
  purpose: string;
  effects: string[];
  /** Zonder venster uitvoeren met de standaardwaarden (Taak afronden). */
  quick?: boolean;
  fields: ActionField[];
}

const FILE = actionsJson as unknown as { actions: Record<string, Omit<ActionDef, "id">>; aliases: Record<string, string> };

/** De actie achter een knop; sommige knoppen starten dezelfde actie ("Pand toevoegen" = "Nieuw pand"). */
export function getAction(id: string): ActionDef | null {
  const target = FILE.aliases[id] ?? id;
  const def = FILE.actions[target];
  return def ? { id: target, ...def } : null;
}

export type FormValue = string | string[];
export type Option = { value: string; label: string };
type Ctx = Partial<ActionContext>;
/** Matchresultaten per woonvraag (uit Panden matchen), voor de shortlist en de standaardwaarden. */
export type Matches = Record<string, { id: string; address: string; score: number; reasons: string[] }[]>;

export const activeContract = (c: CrmRecord) => ACTIVE_CONTRACT_PHASES.includes(str(c, "Fase"));
export const pendingContract = (c: CrmRecord) => !ACTIVE_CONTRACT_PHASES.includes(str(c, "Fase")) && str(c, "Fase") !== "Afgesloten";
export const contractProperty = (c: CrmRecord | null) => firstId(c, "Pand");

const byLabel = (a: Option, b: Option) => a.label.localeCompare(b.label, "nl");

// ---------- Keuzelijsten met records ----------

const opt = {
  properties: (filter: (p: CrmRecord) => boolean = () => true) => (crm: Crm) =>
    crm
      .all("properties")
      .filter(filter)
      .map((r) => ({ value: r.id, label: `${str(r, "Adres")}${str(r, "Status") ? ` · ${str(r, "Status")}` : ""}` }))
      .sort(byLabel),
  contacts: (roles?: string[]) => (crm: Crm) =>
    crm
      .all("contacts")
      .filter((c) => !roles || roles.includes(str(c, "Rol")))
      .map((c) => ({ value: c.id, label: `${str(c, "Naam")}${str(c, "Rol") ? ` · ${str(c, "Rol")}` : ""}` }))
      .sort(byLabel),
  companies: () => (crm: Crm) => crm.all("companies").map((r) => ({ value: r.id, label: str(r, "Naam") })).sort(byLabel),
  requests: () => (crm: Crm) => crm.all("requests").filter((r) => !["Geplaatst", "Verloren"].includes(str(r, "Fase"))).map((r) => ({ value: r.id, label: `${str(r, "Aanvrager")} · ${str(r, "Fase") || "—"}` })),
  contracts: (filter: (c: CrmRecord) => boolean) => (crm: Crm) =>
    crm
      .all("contracts")
      .filter(filter)
      .map((r) => ({ value: r.id, label: `${str(r, "Nummer")} · ${crm.names("contacts", r.fields.Huurder) || "—"} · ${crm.names("properties", r.fields.Pand) || "—"}` })),
  tickets: () => (crm: Crm) => crm.all("tickets").filter(OPEN_TICKET).map((r) => ({ value: r.id, label: `${str(r, "Nummer")} · ${str(r, "Omschrijving")}` })),
  partners: () => (crm: Crm) => crm.all("partners").map((r) => ({ value: r.id, label: `${str(r, "Naam")} · ${str(r, "Dienst")}` })),
  tasks: () => (crm: Crm) => crm.all("tasks").filter(OPEN_TASK).map((r) => ({ value: r.id, label: `${str(r, "Taak")} · ${str(r, "Verantwoordelijke")}` }))
};

const rentable = (p: CrmRecord) => !["Prospect", "Pandbezoek gepland"].includes(str(p, "Status"));
const depositContract = (c: CrmRecord) => ["Ontvangen", "Te beoordelen", "Betwist", "Gedeeltelijke inhouding"].includes(str(c, "Waarborgstatus")) && ["Uittrede", "Afgesloten", "Verlenging", "Actief"].includes(str(c, "Fase"));

type OptionsFn = (crm: Crm, ctx: Ctx) => Option[];

/** Keuzes per veld ("actie.veld"). Velden met records zonder regel hier tonen alle records van hun tabel. */
const OPTIONS: Record<string, OptionsFn> = {
  "dashboard.newHousingRequest.company": opt.companies(),
  "dashboard.newProperty.owner": opt.contacts(["Eigenaar", "Lead"]),
  "dashboard.newTask.property": opt.properties(),
  "relationships.addContact.company": opt.companies(),
  "relationships.scheduleOwnerIntake.owner": opt.contacts(["Eigenaar", "Lead"]),
  "relationships.sendIntakeForm.contact": opt.contacts(),
  "company.sendAvailability.request": (crm, ctx) =>
    crm
      .linked("requests", "Bedrijf", ctx.companyId)
      .filter((r) => !["Geplaatst", "Verloren"].includes(str(r, "Fase")))
      .map((r) => ({ value: r.id, label: `${str(r, "Aanvrager")} · ${str(r, "Fase")}` })),
  "company.sendAvailability.properties": opt.properties((p) => ["Beschikbaar", "In optie"].includes(str(p, "Status")) || (daysUntil(str(p, "Huur einde")) ?? 999) < 90),
  "company.scheduleFollowup.contact": (crm, ctx) => crm.linked("contacts", "Bedrijf", ctx.companyId).map((c) => ({ value: c.id, label: `${str(c, "Naam")} · ${str(c, "Rol")}` })),
  "company.birthdayCard.contact": (crm) =>
    crm
      .all("contacts")
      .filter((c) => str(c, "Verjaardag"))
      .map((c) => ({ value: c.id, label: `${str(c, "Naam")} · ${fmtDate(str(c, "Verjaardag"), "short")}` })),
  "property.requestDocument.recipient": opt.contacts(),
  "rentals.match.request": opt.requests(),
  "rentals.sendProposal.request": opt.requests(),
  "rentals.sendProposal.properties": opt.properties(rentable),
  "rentals.scheduleViewing.request": opt.requests(),
  "rentals.scheduleViewing.property": opt.properties(rentable),
  "rentals.reserve.request": opt.requests(),
  "rentals.reserve.property": opt.properties(rentable),
  "contract.requestData.contract": opt.contracts(pendingContract),
  "contract.generate.contract": opt.contracts(pendingContract),
  "contract.sendForSignature.contract": opt.contracts(pendingContract),
  "contract.registerDeposit.contract": opt.contracts(pendingContract),
  "contract.planDescription.contract": opt.contracts(pendingContract),
  "contract.planCheckin.contract": opt.contracts(pendingContract),
  "contract.completeCheckin.contract": opt.contracts((c) => str(c, "Fase") === "Check-in" || pendingContract(c)),
  "active.newTicket.property": opt.properties(),
  "active.assignPartner.ticket": opt.tickets(),
  "active.assignPartner.partner": opt.partners(),
  "active.contactOwner.ticket": opt.tickets(),
  "active.closeTicket.ticket": opt.tickets(),
  "active.offerService.property": opt.properties((p) => ["Verhuurd", "Check-in", "Uittrede"].includes(str(p, "Status"))),
  "checkout.startRenewal.contract": opt.contracts(activeContract),
  "checkout.planPreInspection.contract": opt.contracts(activeContract),
  "checkout.planCheckout.contract": opt.contracts(activeContract),
  "checkout.planDeepCleaning.property": opt.properties(),
  "checkout.processDeposit.contract": opt.contracts(depositContract),
  "checkout.makeAvailable.property": opt.properties((p) => ["Uittrede", "Verhuurd"].includes(str(p, "Status"))),
  "planning.completeTask.task": opt.tasks(),
  // Alleen documenten die nog openstaan; vanuit een pand of contract alleen die van dat dossier.
  "property.documentReceived.document": (crm, ctx) =>
    crm
      .all("documents")
      .filter((d) => ["Ontbreekt", "Opgevraagd"].includes(str(d, "Status")))
      .filter((d) => (ctx.contractId ? ids(d, "Contract").includes(ctx.contractId) : ctx.propertyId ? ids(d, "Pand").includes(ctx.propertyId) : true))
      .map((d) => ({ value: d.id, label: `${str(d, "Naam")} · ${str(d, "Status")}` }))
      .sort(byLabel)
};

export function fieldOptions(action: ActionDef, field: ActionField, crm: Crm, ctx: Ctx): Option[] {
  const fn = OPTIONS[`${action.id}.${field.name}`];
  if (fn) return fn(crm, ctx);
  if (field.table) return crm.all(field.table).map((r) => ({ value: r.id, label: crm.name(field.table!, r.id) })).sort(byLabel);
  return (field.options ?? []).map((value) => ({ value, label: field.optionLabels?.[value] ?? value }));
}

// ---------- Standaardwaarden ----------

type DefaultFn = (crm: Crm, ctx: Ctx, matches: Matches) => FormValue | undefined;

const contractOf = (crm: Crm, ctx: Ctx) => crm.get("contracts", ctx.contractId);
const contractVal = (field: string) => (crm: Crm, ctx: Ctx) => str(contractOf(crm, ctx), field);
const fromContext = (key: keyof ActionContext) => (_crm: Crm, ctx: Ctx) => ctx[key] ?? "";
const propertyOwner = (crm: Crm, propertyId: string | null | undefined) => firstId(crm.get("properties", propertyId), "Eigenaar");
const cleaningProperty = (crm: Crm, ctx: Ctx) => ctx.propertyId || contractProperty(contractOf(crm, ctx)) || "";

const DEFAULTS: Record<string, DefaultFn> = {
  "dashboard.newHousingRequest.company": fromContext("companyId"),
  "dashboard.newTask.property": fromContext("propertyId"),
  "relationships.addContact.company": fromContext("companyId"),
  "company.planReview.participants": (crm, ctx) => `${str(crm.get("companies", ctx.companyId), "Contactpersoon")}, Evy`,
  "property.scheduleVisit.participants": (crm, ctx) => {
    const owner = propertyOwner(crm, ctx.propertyId);
    return owner ? `${crm.name("contacts", owner)} · eigenaar` : "";
  },
  "property.requestDocument.recipient": (crm, ctx) => propertyOwner(crm, ctx.propertyId) ?? "",
  ...Object.fromEntries(CHECKLIST.map((item) => [`property.updateChecklist.${item.field}`, ((crm, ctx) => str(crm.get("properties", ctx.propertyId), item.field) || "Ontbreekt") as DefaultFn])),
  "property.updateChecklist.notes": (crm, ctx) => str(crm.get("properties", ctx.propertyId), "Checklist-notities"),
  "property.publish.price": (crm, ctx) => str(crm.get("properties", ctx.propertyId), "Maandhuur"),
  "property.publish.available": (crm, ctx) => str(crm.get("properties", ctx.propertyId), "Beschikbaar vanaf") || todayIso(),
  "rentals.match.request": fromContext("requestId"),
  "rentals.sendProposal.request": fromContext("requestId"),
  "rentals.sendProposal.properties": (_crm, ctx, matches) => (matches[ctx.requestId ?? ""] ?? []).map((m) => m.id),
  "rentals.scheduleViewing.request": fromContext("requestId"),
  "rentals.scheduleViewing.property": (_crm, ctx, matches) => (matches[ctx.requestId ?? ""] ?? [])[0]?.id ?? "",
  "rentals.reserve.request": fromContext("requestId"),
  "rentals.reserve.property": (crm, ctx) => firstId(crm.get("requests", ctx.requestId), "Pand") ?? "",
  "rentals.reserve.start": (crm, ctx) => str(crm.get("requests", ctx.requestId), "Start"),
  "rentals.reserve.end": (crm, ctx) => str(crm.get("requests", ctx.requestId), "Einde"),
  "contract.requestData.contract": fromContext("contractId"),
  "contract.requestData.missing": (crm, ctx) => {
    const value = contractOf(crm, ctx)?.fields["Ontbrekende gegevens"];
    return value === undefined ? "Paspoort / ID\nWerkgeversattest" : String(value);
  },
  "contract.generate.contract": fromContext("contractId"),
  "contract.generate.contractType": contractVal("Type"),
  "contract.generate.start": contractVal("Start"),
  "contract.generate.end": contractVal("Einde"),
  "contract.generate.rent": contractVal("Maandhuur"),
  "contract.generate.deposit": contractVal("Waarborg"),
  "contract.sendForSignature.contract": fromContext("contractId"),
  "contract.sendForSignature.signers": (crm, ctx) => {
    const c = contractOf(crm, ctx);
    if (!c) return "";
    const tenant = crm.names("contacts", c.fields.Huurder);
    const company = crm.names("companies", c.fields.Bedrijf);
    const owner = crm.name("contacts", propertyOwner(crm, contractProperty(c)));
    return [tenant && `${tenant} · huurder`, company && `${company} · betaler`, owner && `${owner} · eigenaar`].filter(Boolean).join("\n");
  },
  "contract.registerDeposit.contract": fromContext("contractId"),
  "contract.registerDeposit.amount": contractVal("Waarborg"),
  "contract.planDescription.contract": fromContext("contractId"),
  "contract.planDescription.date": contractVal("Start"),
  "contract.planDescription.participants": (crm, ctx) => `${crm.names("contacts", contractOf(crm, ctx)?.fields.Huurder)} · huurder`,
  "contract.planCheckin.contract": fromContext("contractId"),
  "contract.planCheckin.date": contractVal("Start"),
  "contract.completeCheckin.contract": fromContext("contractId"),
  "active.newTicket.property": fromContext("propertyId"),
  "active.assignPartner.ticket": fromContext("ticketId"),
  "active.contactOwner.ticket": fromContext("ticketId"),
  "active.contactOwner.estimate": (crm, ctx) => str(crm.get("tickets", ctx.ticketId), "Kostenraming"),
  "active.closeTicket.ticket": fromContext("ticketId"),
  "active.offerService.property": fromContext("propertyId"),
  "active.offerService.customer": (crm, ctx) => crm.names("contacts", crm.get("properties", ctx.propertyId)?.fields.Huurder),
  "checkout.startRenewal.contract": fromContext("contractId"),
  "checkout.startRenewal.tenantIntent": contractVal("Verlenging"),
  "checkout.planPreInspection.contract": fromContext("contractId"),
  "checkout.planCheckout.contract": fromContext("contractId"),
  "checkout.planCheckout.date": contractVal("Einde"),
  "checkout.planCheckout.participants": (crm, ctx) => crm.names("contacts", contractOf(crm, ctx)?.fields.Huurder),
  "checkout.planDeepCleaning.property": cleaningProperty,
  "checkout.processDeposit.contract": fromContext("contractId"),
  "checkout.processDeposit.deposit": contractVal("Waarborg"),
  "checkout.makeAvailable.property": cleaningProperty,
  "checkout.makeAvailable.available": (crm, ctx) => str(crm.get("properties", cleaningProperty(crm, ctx)), "Beschikbaar vanaf"),
  "checkout.makeAvailable.price": (crm, ctx) => str(crm.get("properties", cleaningProperty(crm, ctx)), "Maandhuur"),
  "planning.completeTask.task": fromContext("taskId")
};

/**
 * Beginwaarden van het formulier, zoals de vorige frontend: de standaardwaarde, anders voor een
 * datum vandaag + (positie + 3) dagen, en bij een verplichte keuzelijst de eerste optie. `preset` (wat
 * de knop al weet, bv. welk document) gaat voor alles.
 */
export function initialValues(action: ActionDef, crm: Crm, ctx: Ctx, matches: Matches, preset: Record<string, FormValue> = {}): Record<string, FormValue> {
  const values: Record<string, FormValue> = {};
  action.fields.forEach((field, index) => {
    let value = preset[field.name] ?? DEFAULTS[`${action.id}.${field.name}`]?.(crm, ctx, matches) ?? field.default ?? "";
    const multiple = field.kind === "multi" || field.kind === "records";
    if (multiple) {
      values[field.name] = Array.isArray(value) ? value : [];
      return;
    }
    if (Array.isArray(value)) value = value[0] ?? "";
    if (!value && field.kind === "date" && !field.noAutoDate) value = addDays(todayIso(), index + 3);
    if (field.kind === "select" || field.kind === "record") {
      const options = fieldOptions(action, field, crm, ctx);
      if (value && !options.some((o) => o.value === value)) value = "";
      if (!value && field.required) value = options[0]?.value ?? "";
    }
    values[field.name] = value;
  });
  return values;
}
